import './styles/graphic.scss';

// ============================================================
// D3 IMPORTS
// Import specific D3 modules for scales, shapes, axes, and data manipulation
// ============================================================
import { scaleTime, scaleLinear } from 'd3-scale';
import { axisBottom, axisLeft } from 'd3-axis';
import {
  area, stack, stackOrderNone, stackOffsetNone, curveMonotoneX,
} from 'd3-shape';
import { extent, max, group } from 'd3-array';
import { timeParse } from 'd3-time-format';
import { format } from 'd3-format';

// ============================================================
// DATA URL
// Remote JSON endpoint containing ICE/CBP detainee data
// ============================================================
const DATA_URL = 'https://ix.cnn.io/data/ice-tracker/trac_detainees_criminality.json';

// ============================================================
// MAIN GRAPHIC FUNCTION
// This is an async function because we need to fetch remote data
// ============================================================
export default async function (props, container, isMobile) {
  // Destructure all configuration values from props (set in config.js)
  const {
    initialWidth,
    initialHeight,
    width,
    height,
    margins,
    colors,
    yTickCount,
    xTickCount,
  } = props;

  // ============================================================
  // DATA FETCHING
  // Fetch JSON data from the remote URL
  // ============================================================
  const response = await fetch(DATA_URL);
  const json = await response.json();

  // ============================================================
  // DATA FILTERING
  // Keep only records where:
  // - authority is "ice" or "cbp" (exclude "total")
  // - criminality is "all" (we want total counts, not broken down by criminal status)
  // ============================================================
  const filtered = json.data.filter(d => (d.authority === 'ice' || d.authority === 'cbp')
    && d.criminality === 'all');

  // ============================================================
  // DATA TRANSFORMATION: Long format → Wide format
  // The raw data is in "long" format with one row per authority per date.
  // d3.stack() requires "wide" format: one row per date with columns for each authority.
  // ============================================================

  // Step 1: Group filtered data by date
  // d3.group() returns a Map where keys are date strings, values are arrays of records
  const groupedByDate = group(filtered, d => d.date);

  // Step 2: Create a date parser for the YYYY-MM-DD format
  const parseDate = timeParse('%Y-%m-%d');

  // Step 3: Transform each group into a single wide-format object
  const wideData = Array.from(groupedByDate, ([dateString, records]) => {
    // Create the base object with a parsed Date
    const row = {
      date: parseDate(dateString),
    };

    // Add each authority's detainee count as a property
    // records is like [{ authority: "ice", detainees: 59644 }, { authority: "cbp", detainees: 11122 }]
    records.forEach((record) => {
      row[record.authority] = record.detainees;
    });

    return row;
  });

  // Step 4: Sort by date ascending (oldest first) for proper chart rendering
  wideData.sort((a, b) => a.date - b.date);

  // ============================================================
  // SCALES
  // Map data values to pixel positions on the chart
  // ============================================================

  // X-scale: Maps dates to horizontal positions
  const xScale = scaleTime()
    .domain(extent(wideData, d => d.date)) // Data range: earliest to latest date
    .range([0, width]); // Pixel range: left to right of chart area

  // Y-scale: Maps detainee counts to vertical positions
  // For stacked areas, the max is the sum of both authorities
  const yScale = scaleLinear()
    .domain([0, max(wideData, d => (d.ice || 0) + (d.cbp || 0))]) // 0 to max total
    .range([height, 0]) // Inverted: 0 at bottom, max at top
    .nice(); // Round to nice tick values

  // ============================================================
  // STACK GENERATOR
  // d3.stack() computes the baseline (y0) and topline (y1) for each layer
  // This allows areas to stack on top of each other
  // ============================================================
  const keys = ['cbp', 'ice']; // Order matters: first key is bottom layer

  const stackGenerator = stack()
    .keys(keys) // Which properties to stack
    .order(stackOrderNone) // Keep the order as specified
    .offset(stackOffsetNone); // Start from zero baseline

  // Generate stacked data structure
  // Result: array of layers, each layer has [y0, y1] pairs for each data point
  const stackedData = stackGenerator(wideData);

  // ============================================================
  // AREA GENERATOR
  // Creates SVG path strings from stacked data
  // Each area is defined by its baseline (y0) and topline (y1)
  // ============================================================
  const areaGenerator = area()
    .x(d => xScale(d.data.date)) // X position from date
    .y0(d => yScale(d[0])) // Bottom of area (baseline)
    .y1(d => yScale(d[1])) // Top of area (topline)
    .curve(curveMonotoneX); // Smooth curve that preserves monotonicity

  // ============================================================
  // SVG SETUP
  // Create the main SVG element and chart group
  // ============================================================
  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', initialHeight);

  // Create a group element offset by margins for the chart area
  const chartGroup = svg.append('g')
    .attr('class', 'chart-area')
    .attr('transform', `translate(${margins.left}, ${margins.top})`);

  // ============================================================
  // PRESIDENTIAL ADMINISTRATION RANGES
  // Shaded background regions to indicate Trump administrations
  // Labels positioned at the midpoint of each term
  // ============================================================
  const bidenStart = new Date('2021-01-20');
  const trump2Start = new Date('2025-01-20');

  // Get the x-scale domain boundaries
  const [dataStart, dataEnd] = xScale.domain();

  // Trump 1st term: from data start to Biden inauguration
  if (dataStart < bidenStart) {
    const trump1End = bidenStart < dataEnd ? bidenStart : dataEnd;

    chartGroup.append('rect')
      .attr('class', 'admin-range trump')
      .attr('x', xScale(dataStart))
      .attr('y', 0)
      .attr('width', xScale(trump1End) - xScale(dataStart))
      .attr('height', height)
      .attr('fill', '#5e5e5e')
      .attr('opacity', 0.1);

    // Trump 1st term label - positioned at midpoint of the range
    const trump1MidX = (xScale(dataStart) + xScale(trump1End)) / 2;
    chartGroup.append('text')
      .attr('class', 'admin-label')
      .attr('x', trump1MidX)
      .attr('y', 15)
      .attr('text-anchor', 'middle')
      .text('Trump');
  }

  // Biden label - positioned at midpoint between the two Trump inauguration dates
  // (even though Biden's term has no shading)
  if (dataStart < trump2Start && dataEnd > bidenStart) {
    const bidenStartClamped = bidenStart > dataStart ? bidenStart : dataStart;
    const bidenEndClamped = trump2Start < dataEnd ? trump2Start : dataEnd;
    const bidenMidX = (xScale(bidenStartClamped) + xScale(bidenEndClamped)) / 2;

    chartGroup.append('text')
      .attr('class', 'admin-label')
      .attr('x', bidenMidX)
      .attr('y', 15)
      .attr('text-anchor', 'middle')
      .text('Biden');
  }

  // Trump 2nd term: from Trump 2nd inauguration to data end
  if (dataEnd > trump2Start) {
    const trump2StartClamped = trump2Start > dataStart ? trump2Start : dataStart;

    chartGroup.append('rect')
      .attr('class', 'admin-range trump')
      .attr('x', xScale(trump2StartClamped))
      .attr('y', 0)
      .attr('width', xScale(dataEnd) - xScale(trump2StartClamped))
      .attr('height', height)
      .attr('fill', '#5e5e5e')
      .attr('opacity', 0.1);

    // Trump 2nd term label - positioned at midpoint of the range
    const trump2MidX = (xScale(trump2StartClamped) + xScale(dataEnd)) / 2;
    chartGroup.append('text')
      .attr('class', 'admin-label')
      .attr('x', trump2MidX)
      .attr('y', 15)
      .attr('text-anchor', 'middle')
      .text('Trump');
  }

  // ============================================================
  // RENDER Y-AXIS WITH GRIDLINES
  // CNN style: horizontal gridlines extend across the chart
  // ============================================================
  const yAxis = axisLeft(yScale)
    .ticks(yTickCount)
    .tickSize(-width) // Negative tick size creates gridlines across chart
    .tickFormat(format(',')); // Format numbers with commas

  const yAxisGroup = chartGroup.append('g')
    .attr('class', 'axis y-axis')
    .call(yAxis);

  // Style the y-axis per CNN guidelines
  // Gridlines: #B1B1B1, 0.5px
  yAxisGroup.selectAll('.tick line')
    .attr('stroke', '#B1B1B1')
    .attr('stroke-width', 0.5);

  // Labels: #A6A6A6, 12px, light weight
  yAxisGroup.selectAll('.tick text')
    .attr('fill', '#A6A6A6')
    .style('font-size', '12px')
    .style('font-weight', 300);

  // Remove axis domain line (CNN minimalist style)
  yAxisGroup.select('.domain').remove();

  // ============================================================
  // RENDER X-AXIS
  // CNN style: minimal, no gridlines, just date labels
  // ============================================================
  const xAxis = axisBottom(xScale)
    .ticks(xTickCount);

  const xAxisGroup = chartGroup.append('g')
    .attr('class', 'axis x-axis')
    .attr('transform', `translate(0, ${height})`)
    .call(xAxis);

  // Style the x-axis per CNN guidelines
  xAxisGroup.selectAll('.tick text')
    .attr('fill', '#A6A6A6')
    .style('font-size', '12px')
    .style('font-weight', 300);

  // Remove x-axis line and tick marks for cleaner look
  xAxisGroup.select('.domain').remove();
  xAxisGroup.selectAll('.tick line').remove();

  // ============================================================
  // RENDER STACKED AREAS
  // Draw the area paths for each authority layer
  // ============================================================
  chartGroup.selectAll('.area')
    .data(stackedData)
    .join('path')
    .attr('class', d => `area area-${d.key}`)
    .attr('d', areaGenerator)
    .attr('fill', d => colors[d.key])
    .attr('opacity', 0.9);

  // ============================================================
  // DIRECT AREA LABELS
  // CNN style prefers direct labels over legends
  // Position labels inside the area when thick enough, outside when too thin
  // ============================================================
  const lastPoint = wideData[wideData.length - 1];

  // Threshold for when label fits inside the area (in detainees)
  const labelThreshold = 7000;
  const labelOffset = 5; // Pixels from the right edge when positioned outside

  // Calculate vertical positions for labels (center of each stacked area)
  const cbpMidY = yScale((lastPoint.cbp || 0) / 2);
  const iceMidY = yScale((lastPoint.cbp || 0) + (lastPoint.ice || 0) / 2);

  // CBP label - conditional positioning based on area thickness
  const cbpValue = lastPoint.cbp || 0;
  const cbpLabelX = cbpValue < labelThreshold ? width + labelOffset : width - 27;
  const cbpLabelClass = cbpValue < labelThreshold ? 'area-label' : 'area-label-cbp';

  chartGroup.append('text')
    .attr('class', cbpLabelClass)
    .attr('x', cbpLabelX)
    .attr('y', cbpMidY)
    .attr('dy', '0.35em')
    .text('CBP');

  // ICE label - conditional positioning based on area thickness
  const iceValue = lastPoint.ice || 0;
  const iceLabelX = iceValue < labelThreshold ? width + labelOffset : width - 25;
  const iceLabelClass = iceValue < labelThreshold ? 'area-label' : 'area-label-ice';

  chartGroup.append('text')
    .attr('class', iceLabelClass)
    .attr('x', iceLabelX)
    .attr('y', iceMidY)
    .attr('dy', '0.35em')
    .text('ICE');

  // ============================================================
  // RETURN METADATA
  // Return values needed for dynamic dek and footnote in index.js
  // ============================================================
  return {
    totalDetainees: json.totalDetainees,
    pctIncreaseTrump: json.pctIncreaseTrump,
    lastUpdatedStr: json.lastUpdatedStr,
    lastUpdated: json.lastUpdated,
  };
}
